import { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { locationsAPI } from '@/services/api';

/**
 * AddressForm Component
 * Structured address form with Country → State → District → Area → Pincode
 * 
 * Props:
 * - address: object - current address values {address_line1, address_line2, country, state, district, area, pincode}
 * - onChange: (address) => void - callback when any field changes
 * - disabled: boolean - disable all inputs
 */
const AddressForm = ({ 
  address = {}, 
  onChange,
  disabled = false 
}) => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Default values
  const currentAddress = {
    address_line1: address.address_line1 || '',
    address_line2: address.address_line2 || '',
    country: address.country || 'India',
    state: address.state || '',
    district: address.district || '',
    city: address.city || '', // Keep for backward compatibility
    area: address.area || '',
    pincode: address.pincode || ''
  };

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await locationsAPI.getCountries();
        setCountries(response.data);
      } catch (error) {
        // Default to India if API fails
        setCountries([{ code: 'IN', name: 'India' }]);
      }
    };
    fetchCountries();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (currentAddress.country === 'India') {
        setLoadingStates(true);
        try {
          const response = await locationsAPI.getStates('IN');
          setStates(response.data);
        } catch (error) {
          setStates([]);
        } finally {
          setLoadingStates(false);
        }
      } else {
        setStates([]);
      }
    };
    fetchStates();
  }, [currentAddress.country]);

  // Fetch districts when state changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (currentAddress.state) {
        setLoadingDistricts(true);
        try {
          // First find state ID from states list
          const stateObj = states.find(s => s.name === currentAddress.state);
          if (stateObj) {
            const response = await locationsAPI.getDistricts(stateObj.id);
            // API returns paginated {items: [...]} or dropdown array
            const districtData = response.data?.items || response.data || [];
            // Extract district names for display
            const districtNames = districtData.map(d => typeof d === 'string' ? d : d.name);
            setDistricts(districtNames);
          } else {
            setDistricts([]);
          }
        } catch (error) {
          setDistricts([]);
        } finally {
          setLoadingDistricts(false);
        }
      } else {
        setDistricts([]);
      }
    };
    fetchDistricts();
  }, [currentAddress.state, states]);

  const handleFieldChange = (field, value) => {
    const newAddress = { ...currentAddress, [field]: value };
    
    // Reset dependent fields when parent changes
    if (field === 'country') {
      newAddress.state = '';
      newAddress.district = '';
    }
    if (field === 'state') {
      newAddress.district = '';
    }
    
    onChange(newAddress);
  };

  return (
    <div className="space-y-4">
      {/* Address Line 1 */}
      <div className="space-y-2">
        <Label className="text-gray-700">Address Line 1</Label>
        <Input
          value={currentAddress.address_line1}
          onChange={(e) => handleFieldChange('address_line1', e.target.value)}
          placeholder="Building, Street address"
          className="bg-gray-50 border-gray-300"
          disabled={disabled}
          data-testid="address-line1-input"
        />
      </div>

      {/* Address Line 2 */}
      <div className="space-y-2">
        <Label className="text-gray-700">Address Line 2</Label>
        <Input
          value={currentAddress.address_line2}
          onChange={(e) => handleFieldChange('address_line2', e.target.value)}
          placeholder="Landmark, Near by"
          className="bg-gray-50 border-gray-300"
          disabled={disabled}
          data-testid="address-line2-input"
        />
      </div>

      {/* Country & State */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-gray-700">Country *</Label>
          <Select 
            value={currentAddress.country} 
            onValueChange={(v) => handleFieldChange('country', v)}
            disabled={disabled}
          >
            <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="country-select">
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {countries.map(c => (
                <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-700">State *</Label>
          <Select 
            value={currentAddress.state || "select"} 
            onValueChange={(v) => handleFieldChange('state', v === "select" ? "" : v)}
            disabled={disabled || loadingStates}
          >
            <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="state-select">
              <SelectValue placeholder={loadingStates ? "Loading..." : "Select State"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 max-h-60">
              <SelectItem value="select">Select State</SelectItem>
              {states.map(s => (
                <SelectItem key={s.code} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* District & Area */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-gray-700">District *</Label>
          <Select 
            value={currentAddress.district || "select"} 
            onValueChange={(v) => handleFieldChange('district', v === "select" ? "" : v)}
            disabled={disabled || loadingDistricts || !currentAddress.state}
          >
            <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="district-select">
              <SelectValue placeholder={
                loadingDistricts ? "Loading..." : 
                !currentAddress.state ? "Select state first" : 
                "Select District"
              } />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 max-h-60">
              <SelectItem value="select">Select District</SelectItem>
              {districts.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-700">Area / Locality</Label>
          <Input
            value={currentAddress.area}
            onChange={(e) => handleFieldChange('area', e.target.value)}
            placeholder="Area name"
            className="bg-gray-50 border-gray-300"
            disabled={disabled}
            data-testid="area-input"
          />
        </div>
      </div>

      {/* Pincode */}
      <div className="space-y-2">
        <Label className="text-gray-700">Pincode *</Label>
        <Input
          value={currentAddress.pincode}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
            handleFieldChange('pincode', val);
          }}
          placeholder="6-digit pincode"
          className="bg-gray-50 border-gray-300 max-w-xs"
          disabled={disabled}
          maxLength={6}
          data-testid="pincode-input"
        />
      </div>
    </div>
  );
};

export default AddressForm;
